import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import styles from "../css/dropoutVolunteerList.module.scss";
import { DROPOUT_VOLUNTEER_PATHS } from "../../../../Routes/paths";
import { useGlobalStudy } from "../../../../context/StudyContext";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";
import StatCard from "../../../../Components/Common/StatCard";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import { dropoutVolunteerColumns } from "../helper/dropoutvolunteer.constant";
import { PERMISSION_MODULES, PERMISSION_ACTIONS } from "../../../../common/constants/PermissionConstants";
import { useDropoutVolunteerService } from "../api/useDropoutVolunteerQuery";
import { perPageData, GetUserData, showToastMessage } from "../../../../common/service/CommonService";
import PinVerificationModal from "../../../Pharmacy/DailyReconsilation/components/PinVerificationModal";
import { queryClient } from "api/queryClient";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import CommonButton from "../../../../Components/Common/CommonButton";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";


const DropoutVolunteerList: React.FC = () => {
  // -------------------------------------------------------------------------------------------
  // 1. Hooks & Context
  // -------------------------------------------------------------------------------------------
  const navigate = useNavigate();
  const { selectedStudy: globalStudy } = useGlobalStudy();
  const userData = GetUserData();

  // -------------------------------------------------------------------------------------------
  // 2. States
  // -------------------------------------------------------------------------------------------
  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setcurrentPageNumber] = useState<number>(1);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  // Audit States
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  // -------------------------------------------------------------------------------------------
  // 3. Effects
  // -------------------------------------------------------------------------------------------
  useEffect(() => {
    document.title = "Dropout Management | ZENTIXS ORBIT";
  }, []);

  const prevStudyRef = useRef(globalStudy?.studyid);
  useEffect(() => {
    if (prevStudyRef.current !== globalStudy?.studyid) {
      prevStudyRef.current = globalStudy?.studyid;
      setcurrentPageNumber(1);
    }
  }, [globalStudy?.studyid]);

  // -------------------------------------------------------------------------------------------
  // 4. API Queries & Mutations
  // -------------------------------------------------------------------------------------------
  const { SelectAllQuery, VerifyMutation, DeleteMutation } = useDropoutVolunteerService({
    searchText: searchText,
    studyid: Number(globalStudy?.studyid),
    currentPage: currentPageNumber,
    pageSize: perPageData,
    enableSelectAll: !!globalStudy?.studyid,
  });

  const { SelectAllMutation: AuditMutation } = useAuditService();

  const rawData = SelectAllQuery?.data?.data || [];
  const apiRecords = rawData.filter((r: any) => !r.summarycard);
  const summaryData = rawData.find((r: any) => r.summarycard)?.summarycard;
  const totalRecords = apiRecords[0]?.totalrecords || 0;
  const isLoading = SelectAllQuery?.isLoading || SelectAllQuery?.isFetching;

  // -------------------------------------------------------------------------------------------
  // 5. Data Processing & Derived States
  // -------------------------------------------------------------------------------------------
  const totalDropouts = summaryData?.totaldropouts ?? totalRecords;
  const pendingApproval = summaryData?.pendingdropouts ?? apiRecords.filter((r: any) => {
    const status = r.statustext || r.status || "";
    return status.toLowerCase() === "pending" || status.toLowerCase() === "p";
  }).length;
  const verifiedDropouts = summaryData?.verifieddropouts ?? apiRecords.filter((r: any) => r.isverified).length;

  // -------------------------------------------------------------------------------------------
  // 6. Handlers & Actions
  // -------------------------------------------------------------------------------------------
  const toggleDeleteModal = () => {
    setDeleteModal((p) => !p);
    setShowDeleteReason(false);
  };
  const toggleDeleteReason = () => setShowDeleteReason((p) => !p);

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setcurrentPageNumber(1);
  };

  const handleVerifyClick = (item: any) => {
    setSelectedRecord(item);
    setVerifyModal(true);
  };

  const handlePinVerify = (password: string) => {
    const payload = {
      verifypin: password,
      volunteerdropoutid: Number(selectedRecord?.volunteerdropoutid || selectedRecord?.id),
      verifiedby: Number(userData?.userid),
    };

    VerifyMutation.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["dropout-volunteer-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", response?.message || "Record verified successfully");
        setVerifyModal(false);
        setSelectedRecord(null);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message ?? error ?? "Failed to verify record");
      },
    });
  };

  const handleDeleteClick = (item: any) => {
    setSelectedRecord(item);
    toggleDeleteModal();
  };

  const confirmDelete = (reasonPayload: { id: number | null; reason: string }) => {
    if (!selectedRecord) return;

    DeleteMutation.mutate(
      {
        volunteerdropoutid: selectedRecord.volunteerdropoutid,
        updatereason: reasonPayload.reason,
      },
      {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["dropout-volunteer-select-all"] });
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          showToastMessage("success", res?.message || "Record deleted successfully");
          setSelectedRecord(null);
          toggleDeleteModal();
          toggleDeleteReason();
        },
        onError: (err: any) => {
          showToastMessage("error", err?.message ?? err ?? "Failed to delete record");
        },
      }
    );
  };

  const getRowActions = (row: any): any => {
    const isVerified = row?.isverified;
    const actions: any = {};

    if (!isVerified) {
      actions.onVerify = (r: any) => handleVerifyClick(r);
      actions.onEdit = (r: any) => navigate(DROPOUT_VOLUNTEER_PATHS.EDIT.replace(":id", (r.volunteerdropoutid).toString()));
      actions.onDelete = (r: any) => handleDeleteClick(r);
    }
    return actions;
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Volunteer dropout",
      studyid: Number(globalStudy?.studyid)
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Volunteer_dropout_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  // -------------------------------------------------------------------------------------------
  // 7. Render
  // -------------------------------------------------------------------------------------------

  return (
    <div className="page-content">
      <Container fluid className="pb-4">
        {!globalStudy?.studyid ? (
          <div>
            <NoStudySelectedPlaceholder moduleName="dropout volunteer list" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mt-1 mb-3 px-2">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>Dropout Management</h5>
                </div>
                <p className={styles.pageHeaderSubtitle}>
                  Manage subject dropout scenarios with appropriate status tracking and approvals.
                </p>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.DROPOUT_VOLUNTEER}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600"
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
              </div>
            </div>

            {/* Stat Cards */}
            <Row className="g-3 mb-3 px-1">
              <Col md={4}>
                <StatCard
                  title="TOTAL DROPOUTS"
                  value={totalDropouts}
                  subtext={`Study ${globalStudy?.studyname || "BE-2024-07"} - All periods`}
                  icon={<i className="ri-user-unfollow-line" />}
                  bgColor="#eff6ff"
                  iconColor="#2563eb"
                  trendType="info"
                  subtextIcon="ri-stack-line"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="PENDING DROPOUTS"
                  value={pendingApproval}
                  subtext="Awaiting review"
                  icon={<i className="ri-time-line" />}
                  bgColor="#fffbeb"
                  iconColor="#d97706"
                  trendType="negative"
                  subtextIcon="ri-error-warning-line"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="VERIFIED DROPOUTS"
                  value={verifiedDropouts}
                  subtext="Verified records"
                  icon={<i className="ri-checkbox-circle-line" />}
                  bgColor="#f0fdf4"
                  iconColor="#16a34a"
                  trendType="success"
                  subtextIcon="ri-checkbox-circle-line"
                />
              </Col>
            </Row>

            {/* Table List Panel */}
            <div className={styles.listPanel}>
              <div className={styles.panelHeader}>
                <div className="d-flex align-items-center gap-3">
                  <h2>
                    <i className="ri-user-unfollow-line me-2 text-primary fs-18" />
                    Dropout Records
                  </h2>
                </div>
                <CommonSearch onSearch={onSearch} searchText={searchText} />
              </div>

              <div className="px-4 py-2">
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.DROPOUT_VOLUNTEER}
                  columns={dropoutVolunteerColumns}
                  data={apiRecords}
                  loading={isLoading}
                  pageSize={perPageData}
                  currentPage={currentPageNumber}
                  totalRecords={totalRecords}
                  onPageChange={setcurrentPageNumber}
                  actions={getRowActions}
                />
              </div>
            </div>
          </>
        )}
        <PinVerificationModal
          isOpen={verifyModal}
          toggle={() => setVerifyModal(false)}
          onVerify={handlePinVerify}
          isLoading={VerifyMutation.isPending}
          title="Verify Dropout Record"
          description={
            <>
              Please enter your password to verify dropout record for volunteer:{" "}
              <strong>{selectedRecord?.initials || selectedRecord?.fullname}</strong>
            </>
          }
          isInvalid={VerifyMutation.isError}
        />
        {/* Delete Modal */}
        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={toggleDeleteModal}
            recordId={selectedRecord?.volunteerdropoutid || selectedRecord?.id}
            recordName={selectedRecord?.fullname || selectedRecord?.initials}
            onDeleteClick={confirmDelete}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={toggleDeleteReason}
            isLoading={DeleteMutation.isPending}
          />
        )}
        {/* File Preview Modal */}
        {isPreviewOpen && (
          <FilePreviewModal
            file={previewFile}
            isOpen={isPreviewOpen}
            size={"xl"}
            onClose={() => {
              setIsPreviewOpen(false);
              setPreviewFile(null);
            }}
          />
        )}
      </Container>
    </div>
  );
};

export default DropoutVolunteerList;
